import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  intervalLabel,
  toIsoDate,
  type AdminOrgDetail,
  type BillingSummary,
  type GrantKind,
  type PlanInterval,
  type UsageAnalytics,
  type OrgTokenSeries,
  type OrgTokenUsage,
} from "@currencycore/shared";
import { grantLimit, getOrg, getOrgBilling, getOrgTokens, getUsage, refundInvoice, setOrgAi, setOrgAuditLogs, setOrgCap } from "../lib/api.ts";
import { PlanEditor } from "../components/PlanEditor.tsx";
import { MembersList } from "../components/MembersList.tsx";
import { AreaChart, compact } from "../components/Charts.tsx";
import { Select } from "../components/Select.tsx";
import { PlanBadge, StatusBadge } from "../components/Badges.tsx";
import { useConfirm } from "../components/useConfirm.tsx";
import { OrgSsoSection } from "../components/OrgSsoSection.tsx";
import { toast } from "../components/Toast.tsx";
import { dashboardOrgUrl, errMsg, fmtMoney, fmtDate } from "../lib/ui.ts";

const RANGES = [
  { days: 7, label: "7d" },
  { days: 30, label: "30d" },
  { days: 90, label: "90d" },
];
const GRAN = [
  { value: "day", label: "Daily" },
  { value: "week", label: "Weekly" },
  { value: "month", label: "Monthly" },
];

function addDays(iso: string, days: number): string {
  const d = new Date(`${iso}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

const nf = (n: number) => n.toLocaleString("en-US");

export function OrgDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [detail, setDetail] = useState<AdminOrgDetail | null>(null);
  const [billing, setBilling] = useState<BillingSummary | null>(null);
  const [usage, setUsage] = useState<UsageAnalytics | null>(null);
  const [tokens, setTokens] = useState<{ usage: OrgTokenUsage; series: OrgTokenSeries } | null>(null);
  const [days, setDays] = useState(30);
  const [granularity, setGranularity] = useState("day");
  const [bonus, setBonus] = useState("");
  const [grantKind, setGrantKind] = useState<GrantKind>("once");
  const [until, setUntil] = useState("");
  const [months, setMonths] = useState("");
  const [adminCap, setAdminCap] = useState("");
  const [orgCap, setOrgCapInput] = useState("");
  const { ask, dialog } = useConfirm();

  useEffect(() => {
    if (!id) return;
    getOrg(id).then(setDetail).catch((e) => toast(errMsg(e, "Failed to load organization.")));
    getOrgBilling(id).then(setBilling).catch(() => {});
    getOrgTokens(id).then(setTokens).catch(() => {});
  }, [id]);

  useEffect(() => {
    if (!id) return;
    const to = toIsoDate();
    const from = addDays(to, -(days - 1));
    getUsage({ org: id, from, to, granularity }).then(setUsage).catch(() => {});
  }, [id, days, granularity]);

  useEffect(() => {
    setAdminCap(detail?.adminHardCapRequests != null ? String(detail.adminHardCapRequests) : "");
    setOrgCapInput(detail?.hardCapRequests != null ? String(detail.hardCapRequests) : "");
  }, [detail]);

  if (!detail) {
    return (
      <div className="page-loading">
        <span className="spinner spinner-lg" />
      </div>
    );
  }

  const period = toIsoDate().slice(0, 7);
  const sub = detail.subscription;
  const grant = detail.grant;
  const grantActive = grant?.active ? grant.requests : 0;
  const effLimit = detail.effectiveMonthlyLimit;
  const dashUrl = dashboardOrgUrl(detail.id);

  function submitGrant(clear: boolean) {
    const add = clear ? 0 : Math.max(0, parseInt(bonus || "0", 10));
    if (add > 0 && grantKind === "until" && !/^\d{4}-\d{2}$/.test(until)) {
      toast("Enter an end month as YYYY-MM.");
      return;
    }
    if (add > 0 && grantKind === "months" && parseInt(months || "0", 10) < 1) {
      toast("Enter the number of months.");
      return;
    }
    const note =
      add <= 0
        ? ""
        : grantKind === "until"
          ? ` each month through ${until}`
          : grantKind === "months"
            ? ` for ${months} month(s)`
            : " (this period only)";
    ask({
      title: add > 0 ? "Grant extra quota?" : "Clear grant?",
      message:
        add > 0 ? (
          <>
            Grant <strong>{nf(add)}</strong> extra requests{note} to <strong>{detail!.name}</strong>?
            This doesn't change the plan; it adds on top of the plan limit.
          </>
        ) : (
          <>Clear the compensation grant for <strong>{detail!.name}</strong>?</>
        ),
      confirmLabel: add > 0 ? "Grant" : "Clear grant",
      run: async () => {
        try {
          const d = await grantLimit(id!, {
            addRequests: add,
            kind: grantKind,
            ...(grantKind === "until" ? { untilPeriod: until } : {}),
            ...(grantKind === "months" ? { months: Math.max(1, parseInt(months || "1", 10)) } : {}),
          });
          setDetail(d);
          setBonus("");
          toast("Grant updated.", true);
        } catch (e) {
          toast(errMsg(e, "Could not update the grant."));
        }
      },
    });
  }

  function confirmRefund(inv: BillingSummary["invoices"][number]) {
    ask({
      title: "Refund this invoice?",
      message: (
        <>
          Refund <strong>{fmtMoney(inv.amountCents, inv.currency)}</strong> to <strong>{detail!.name}</strong> via Dodo?
          This starts a refund with the payment provider and can't be undone here.
        </>
      ),
      confirmLabel: "Refund",
      danger: true,
      run: async () => {
        try {
          setBilling(await refundInvoice(id!, { invoiceId: inv.id }));
          toast("Refund initiated.", true);
        } catch (e) {
          toast(errMsg(e, "Could not initiate the refund."));
        }
      },
    });
  }

  function submitCap() {
    ask({
      title: "Update hard cap",
      message: <>Set the admin ceiling and/or the org’s cap. The org can’t set a cap above the ceiling.</>,
      confirmLabel: "Save cap",
      run: async () => {
        try {
          const d = await setOrgCap(id!, {
            adminRequests: adminCap === "" ? null : Number(adminCap),
            orgRequests: orgCap === "" ? null : Number(orgCap),
          });
          setDetail(d);
          toast("Hard cap updated.", true);
        } catch (e) {
          toast(errMsg(e, "Could not update the hard cap."));
        }
      },
    });
  }

  async function toggleAi(enabled: boolean) {
    try {
      setDetail(await setOrgAi(id!, enabled));
      toast(enabled ? "AI enabled for this org." : "AI disabled for this org.", true);
    } catch (e) {
      toast(errMsg(e, "Could not update AI access."));
    }
  }

  async function setAudit(override: boolean | null) {
    try {
      setDetail(await setOrgAuditLogs(id!, override));
      toast(
        override === null
          ? "Audit logs now follow the plan default."
          : override
            ? "Audit logs forced on for this org."
            : "Audit logs forced off for this org.",
        true,
      );
    } catch (e) {
      toast(errMsg(e, "Could not update audit-log access."));
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <Link to="/organizations" className="back-link">← Organizations</Link>
          <h1>{detail.name}</h1>
          <p className="muted mono">{detail.id}</p>
        </div>
        <div className="page-head-actions">
          <PlanBadge plan={detail.plan} label={detail.planLabel} />
          {sub.billingInterval && <span className="badge badge-neutral">{intervalLabel(sub.billingInterval as PlanInterval)}</span>}
          <StatusBadge status={sub.status} />
          <a href={dashUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" title={dashUrl}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 3h6v6M10 14 21 3M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
            </svg>
            Open dashboard
          </a>
          <button
            className="btn btn-icon btn-ghost btn-sm"
            title="Copy dashboard link"
            aria-label="Copy dashboard link"
            onClick={() => {
              navigator.clipboard?.writeText(dashUrl);
              toast("Dashboard link copied.", true);
            }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <rect x="9" y="9" width="13" height="13" rx="2" />
              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
            </svg>
          </button>
        </div>
      </div>

      <div className="grid grid-4 stat-grid">
        <StatBox label="Total paid" value={billing ? fmtMoney(billing.totalPaidCents, billing.currency) : "—"} sub={`${billing?.invoiceCount ?? 0} invoices`} />
        <StatBox label="Usage this period" value={nf(detail.currentUsage)} sub={period} />
        <StatBox label="AI this period" value={nf(detail.currentUsageAi)} sub={period} />
        <StatBox label="Effective limit" value={effLimit === null ? "∞" : nf(effLimit)} sub={grantActive > 0 ? `${detail.monthlyLimit === null ? "∞" : nf(detail.monthlyLimit)} + ${nf(grantActive)} grant` : "monthly"} />
        <StatBox label="Members" value={String(detail.members.length)} sub={sub.currentPeriodEnd ? `renews ${fmtDate(sub.currentPeriodEnd)}` : "—"} />
      </div>

      <div className="grid grid-2 detail-grid">
        <div className="card card-pad">
          <PlanEditor detail={detail} onUpdated={setDetail} />
          <section className="drawer-section">
            <h4>Compensation grant</h4>
            {grant ? (
              <p className="hint">
                Current grant: <strong>{nf(grant.requests)}</strong> req ·{" "}
                {grant.kind === "once"
                  ? `one-time (${grant.startPeriod})`
                  : grant.kind === "until"
                    ? `monthly through ${grant.endPeriod}`
                    : `monthly ${grant.startPeriod}-${grant.endPeriod}`}{" "}
                · {grant.active ? "active this period" : "not active this period"}.
              </p>
            ) : (
              <p className="hint">Add extra monthly requests on top of the plan limit, without changing the plan.</p>
            )}
            <div className="custom-grid">
              <div className="field">
                <label className="form-label">Extra requests</label>
                <input className="input" inputMode="numeric" value={bonus} onChange={(e) => setBonus(e.target.value.replace(/[^\d]/g, ""))} placeholder="e.g. 100000" />
              </div>
              <div className="field">
                <label className="form-label">Schedule</label>
                <Select
                  value={grantKind}
                  options={[
                    { value: "once", label: "One-time" },
                    { value: "until", label: "Monthly until…" },
                    { value: "months", label: "For N months" },
                  ]}
                  onChange={(v) => setGrantKind(v as GrantKind)}
                  ariaLabel="Grant schedule"
                />
              </div>
              {grantKind === "until" && (
                <div className="field span-2">
                  <label className="form-label">Until month</label>
                  <input className="input" value={until} onChange={(e) => setUntil(e.target.value)} placeholder="2026-12" />
                </div>
              )}
              {grantKind === "months" && (
                <div className="field span-2">
                  <label className="form-label">Number of months</label>
                  <input className="input" inputMode="numeric" value={months} onChange={(e) => setMonths(e.target.value.replace(/[^\d]/g, ""))} placeholder="e.g. 3" />
                </div>
              )}
            </div>
            <div className="ban-row">
              <button className="btn btn-primary btn-sm" onClick={() => submitGrant(false)}>Apply grant</button>
              {grant && <button className="btn btn-ghost btn-sm" onClick={() => submitGrant(true)}>Clear</button>}
            </div>
          </section>
          <section className="drawer-section">
            <h4>Hard cap</h4>
            <div className="custom-grid">
              <div className="field">
                <label className="form-label">Admin ceiling (requests)</label>
                <input
                  className="input"
                  inputMode="numeric"
                  value={adminCap}
                  onChange={(e) => setAdminCap(e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="blank = none"
                />
              </div>
              <div className="field">
                <label className="form-label">Org cap (requests)</label>
                <input
                  className="input"
                  inputMode="numeric"
                  value={orgCap}
                  onChange={(e) => setOrgCapInput(e.target.value.replace(/[^\d]/g, ""))}
                  placeholder="blank = none"
                />
              </div>
            </div>
            <div className="ban-row">
              <button className="btn btn-primary btn-sm" onClick={submitCap}>Save cap</button>
            </div>
          </section>
          <section className="drawer-section">
            <h4>AI endpoint</h4>
            <p className="hint">
              {detail.aiEnabled
                ? "Enabled. This org can use the natural-language AI endpoint."
                : "Disabled. /v1/ai returns 403 for this org."}
            </p>
            <div className="ban-row">
              <button
                className={detail.aiEnabled ? "btn btn-danger btn-sm" : "btn btn-primary btn-sm"}
                onClick={() => void toggleAi(!detail.aiEnabled)}
              >
                {detail.aiEnabled ? "Disable AI" : "Enable AI"}
              </button>
            </div>
          </section>
          <section className="drawer-section">
            <h4>Audit log</h4>
            <p className="hint">
              {detail.auditLogsEnabled
                ? "Enabled. This org can view its customer audit log."
                : "Disabled. The audit log is hidden for this org."}
              {" "}
              {detail.auditLogsOverride === null
                ? "(following plan default)"
                : `(forced ${detail.auditLogsOverride ? "on" : "off"}, overrides plan)`}
              {" · Retention "}
              {Math.round(detail.auditRetentionDays / 30)} months.
            </p>
            <div className="ban-row" style={{ gap: 6 }}>
              <button
                className={detail.auditLogsOverride === null ? "btn btn-primary btn-sm" : "btn btn-sm"}
                disabled={detail.auditLogsOverride === null}
                onClick={() => void setAudit(null)}
              >
                Inherit from plan
              </button>
              <button
                className={detail.auditLogsOverride === true ? "btn btn-primary btn-sm" : "btn btn-sm"}
                disabled={detail.auditLogsOverride === true}
                onClick={() => void setAudit(true)}
              >
                Force on
              </button>
              <button
                className={detail.auditLogsOverride === false ? "btn btn-danger btn-sm" : "btn btn-sm"}
                disabled={detail.auditLogsOverride === false}
                onClick={() => void setAudit(false)}
              >
                Force off
              </button>
            </div>
          </section>
          <OrgSsoSection orgId={id ?? ""} />
        </div>
        <div className="card card-pad">
          <MembersList orgId={detail.id} members={detail.members} onUpdated={setDetail} />
        </div>
      </div>

      <div className="card card-pad chart-card">
        <div className="section-row between">
          <h3>Usage over time</h3>
          <div className="usage-controls">
            <div className="segmented">
              {RANGES.map((r) => (
                <button key={r.days} className={days === r.days ? "active" : ""} onClick={() => setDays(r.days)}>{r.label}</button>
              ))}
            </div>
            <Select value={granularity} options={GRAN} onChange={setGranularity} ariaLabel="Granularity" />
          </div>
        </div>
        <AreaChart data={(usage?.overTime ?? []).map((p) => ({ label: p.bucket, value: p.requests }))} height={240} />
      </div>

      {/* AI token usage — total / this month / avg, each input / output / total. */}
      <div className="card card-pad chart-card">
        <div className="section-row between">
          <h3>AI tokens</h3>
          <span className="muted">{tokens ? `${tokens.usage.monthsActive} active month(s)` : ""}</span>
        </div>
        <div className="grid grid-3 stat-grid">
          {(
            [
              ["Total (all time)", tokens?.usage.total],
              ["This month", tokens?.usage.month],
              ["Avg per month", tokens?.usage.avgMonthly],
            ] as const
          ).map(([label, t]) => (
            <div key={label} className="card statbox">
              <span className="stat-label">{label}</span>
              <span className="stat-value">{t ? compact(t.totalTokens) : "—"} total</span>
              <span className="stat-sub muted">
                {t ? `in ${compact(t.inputTokens)} · out ${compact(t.outputTokens)}` : ""}
              </span>
            </div>
          ))}
        </div>
        <AreaChart
          data={(tokens?.series.months ?? []).map((m) => ({ label: m.period.slice(2), value: m.totalTokens }))}
          height={200}
        />
      </div>

      <div className="card card-pad">
        <div className="section-row between">
          <h3>Billing history</h3>
          {billing && (
            <span className="muted">
              Total paid: <strong>{fmtMoney(billing.totalPaidCents, billing.currency)}</strong>
            </span>
          )}
        </div>
        {billing && billing.invoices.length > 0 ? (
          <div className="table-wrap">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Description</th>
                  <th className="col-num">Amount</th>
                  <th>Status</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {billing.invoices.map((inv) => (
                  <tr key={inv.id}>
                    <td className="tnum muted">{fmtDate(inv.date)}</td>
                    <td>{inv.description ?? "—"}</td>
                    <td className="col-num tnum">{fmtMoney(inv.amountCents, inv.currency)}</td>
                    <td><InvoiceStatusBadge status={inv.status} /></td>
                    <td>
                      <div className="row gap-sm" style={{ justifyContent: "flex-end" }}>
                        {inv.hostedUrl && inv.hostedUrl !== "#" ? (
                          <a href={inv.hostedUrl} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm">View</a>
                        ) : null}
                        {inv.refundable && (
                          <button className="btn btn-danger btn-sm" onClick={() => confirmRefund(inv)}>Refund</button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="muted">No invoices yet.</p>
        )}
      </div>

      {dialog}
    </>
  );
}

function StatBox({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="card statbox">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
      <span className="stat-sub muted">{sub}</span>
    </div>
  );
}

const INVOICE_STATUS_META: Record<string, { cls: string; label: string }> = {
  paid: { cls: "badge-ok", label: "Paid" },
  open: { cls: "badge-warn", label: "Open" },
  refund_pending: { cls: "badge-warn", label: "Refund initiated" },
  refunded: { cls: "badge-neutral", label: "Refunded" },
  void: { cls: "badge-neutral", label: "Void" },
};

function InvoiceStatusBadge({ status }: { status: BillingSummary["invoices"][number]["status"] }) {
  const m = INVOICE_STATUS_META[status] ?? { cls: "badge-neutral", label: status };
  return <span className={`badge ${m.cls}`}>{m.label}</span>;
}
